import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Users, X, Search, Send, Loader2, Image, Building2, CheckSquare, Square, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/config';

const EMPTY_FILTERS = { search: '', zone: '', propCity: '', propProvince: '', propRegion: '', minProps: '', activeOnly: '', partner: '' };

export const BulkSendsManager = ({ open, onClose, onChanged }) => {
  const token = localStorage.getItem('hw_token');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(null);
  const [notifying, setNotifying] = useState(null);

  const loadGroups = () => {
    setLoading(true);
    fetch(`${API_URL}/api/tickets/office-bulk-list`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setGroups(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Errore caricamento invii multipli'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) loadGroups(); }, [open]);

  if (!open) return null;

  const handleCloseAll = async (subject, openCount) => {
    if (!window.confirm(`Chiudere tutti i ${openCount} ticket aperti di "${subject}"?\n\nResteranno comunque "da leggere" (nuovi) per i responsabili.`)) return;
    setClosing(subject);
    try {
      const res = await fetch(`${API_URL}/api/tickets/office-bulk-close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        loadGroups();
        onChanged && onChanged();
      } else {
        toast.error(data.detail || 'Errore nella chiusura');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
    setClosing(null);
  };

  const handleNotify = async (subject, total) => {
    if (!window.confirm(`Inviare a tutti i ${total} responsabili l'email "Hai ricevuto una comunicazione dall'ufficio" con il testo di "${subject}"?\n\nI ticket verranno anche spostati nella sezione "Comunicazioni da Ufficio" dei responsabili.`)) return;
    setNotifying(subject);
    try {
      const res = await fetch(`${API_URL}/api/tickets/office-bulk-notify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        loadGroups();
        onChanged && onChanged();
      } else {
        toast.error(data.detail || "Errore nell'invio");
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
    setNotifying(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-sends-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Send className="h-5 w-5 text-purple-600" />
            Invii Multipli ai Responsabili
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="bulk-sends-close-btn">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>
        <div className="p-5">
          <p className="text-xs text-slate-500 mb-3">
            Qui trovi gli invii multipli raggruppati per oggetto. Puoi chiudere in blocco tutti i ticket di un invio (per i responsabili resteranno comunque <strong>nuovi da leggere</strong>) oppure usare <strong>Email comunicazione</strong>: sposta i ticket nella sezione "Comunicazioni da Ufficio" dei responsabili e invia a tutti l'email "Hai ricevuto una comunicazione dall'ufficio".
          </p>
          {loading ? (
            <div className="p-6 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</div>
          ) : groups.length === 0 ? (
            <div className="p-6 text-sm text-slate-400 text-center">Nessun invio multiplo trovato</div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {groups.map(g => (
                <div key={g.subject} className="p-3 flex items-center gap-3" data-testid={`bulk-send-group-${g.subject.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{g.subject}</p>
                    <p className="text-xs text-slate-400">
                      {g.total} ticket · {g.last_created ? new Date(g.last_created).toLocaleDateString('it-IT') : ''}{g.created_by ? ` · ${g.created_by}` : ''}
                    </p>
                  </div>
                  {g.open > 0 ? (
                    <>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full shrink-0">{g.open} aperti</span>
                      <Button size="sm" variant="outline" disabled={closing === g.subject}
                        onClick={() => handleCloseAll(g.subject, g.open)}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0"
                        data-testid={`bulk-close-group-btn-${g.subject.replace(/\s+/g, '-').toLowerCase()}`}>
                        {closing === g.subject ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Chiudi tutti'}
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full shrink-0">Tutti chiusi</span>
                  )}
                  <Button size="sm" variant="outline" disabled={notifying === g.subject}
                    onClick={() => handleNotify(g.subject, g.total)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 shrink-0"
                    title="Sposta in Comunicazioni da Ufficio e invia email ai responsabili"
                    data-testid={`bulk-notify-group-btn-${g.subject.replace(/\s+/g, '-').toLowerCase()}`}>
                    {notifying === g.subject ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Email comunicazione</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const BulkTicketModal = ({ open, onClose, onSent, categories }) => {
  const token = localStorage.getItem('hw_token');
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normale', category: '' });
  const [closeAfterSend, setCloseAfterSend] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFilters(EMPTY_FILTERS);
    setSelected(new Set());
    setForm({ subject: '', message: '', priority: 'normale', category: '' });
    setCloseAfterSend(false);
    setAttachments([]);
    setLoadingManagers(true);
    fetch(`${API_URL}/api/tickets/managers-list`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setManagers(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Errore caricamento responsabili'))
      .finally(() => setLoadingManagers(false));
  }, [open]);

  const options = useMemo(() => {
    const zones = new Set(), cities = new Set(), provinces = new Set(), regions = new Set();
    managers.forEach(m => {
      if (m.city) zones.add(m.city);
      if (m.operation_area_city) zones.add(m.operation_area_city);
      (m.property_cities || []).forEach(c => cities.add(c));
      (m.property_provinces || []).forEach(p => provinces.add(p));
      (m.property_regions || []).forEach(r => regions.add(r));
    });
    const s = set => [...set].sort();
    return { zones: s(zones), cities: s(cities), provinces: s(provinces), regions: s(regions) };
  }, [managers]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return managers.filter(m => {
      if (q && !(`${m.name} ${m.email} ${m.phone}`.toLowerCase().includes(q))) return false;
      if (filters.zone && m.city !== filters.zone && m.operation_area_city !== filters.zone) return false;
      if (filters.propCity && !(m.property_cities || []).includes(filters.propCity)) return false;
      if (filters.propProvince && !(m.property_provinces || []).includes(filters.propProvince)) return false;
      if (filters.propRegion && !(m.property_regions || []).includes(filters.propRegion)) return false;
      if (filters.minProps !== '') {
        if (filters.minProps === '0' && m.property_count !== 0) return false;
        if (filters.minProps !== '0' && m.property_count < Number(filters.minProps)) return false;
      }
      if (filters.activeOnly === 'si' && m.active_property_count === 0) return false;
      if (filters.activeOnly === 'no' && m.active_property_count > 0) return false;
      if (filters.partner === 'si' && !m.is_also_partner) return false;
      if (filters.partner === 'no' && m.is_also_partner) return false;
      return true;
    });
  }, [managers, filters]);

  if (!open) return null;

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectAllFiltered = () => setSelected(new Set([...selected, ...filtered.map(m => m.id)]));
  const clearAll = () => setSelected(new Set());
  const allFilteredSelected = filtered.length > 0 && filtered.every(m => selected.has(m.id));

  const handleImageUpload = (e) => {
    Array.from(e.target.files).slice(0, 5).forEach(file => {
      if (!file.type.startsWith('image/')) { toast.error('Solo immagini permesse'); return; }
      if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB per immagine'); return; }
      const reader = new FileReader();
      reader.onloadend = () => setAttachments(prev => [...prev.slice(0, 4), reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const handleSend = async () => {
    if (selected.size === 0) { toast.error('Seleziona almeno un responsabile'); return; }
    if (!form.subject.trim() || !form.message.trim()) { toast.error('Inserisci oggetto e messaggio'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/from-office`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: form.subject,
          message: form.message,
          priority: form.priority,
          category: form.category || undefined,
          target_manager_ids: [...selected],
          attachments,
          close_after_send: closeAfterSend
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        onSent(data);
        onClose();
      } else {
        toast.error(data.detail || 'Errore nella creazione dei ticket');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
    setSending(false);
  };

  const selClass = "w-full p-2 border border-slate-300 rounded-lg text-sm bg-white";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-ticket-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            Ticket Multiplo ai Responsabili
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="bulk-close-btn">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Filtri */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase">Filtra responsabili</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Cerca per nome, email o telefono..." className="pl-10 bg-white" data-testid="bulk-filter-search" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <select value={filters.zone} onChange={e => setFilters(f => ({ ...f, zone: e.target.value }))} className={selClass} data-testid="bulk-filter-zone">
                <option value="">Zona responsabile: tutte</option>
                {options.zones.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
              <select value={filters.propRegion} onChange={e => setFilters(f => ({ ...f, propRegion: e.target.value }))} className={selClass} data-testid="bulk-filter-region">
                <option value="">Regione immobili: tutte</option>
                {options.regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={filters.propProvince} onChange={e => setFilters(f => ({ ...f, propProvince: e.target.value }))} className={selClass} data-testid="bulk-filter-province">
                <option value="">Provincia immobili: tutte</option>
                {options.provinces.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={filters.propCity} onChange={e => setFilters(f => ({ ...f, propCity: e.target.value }))} className={selClass} data-testid="bulk-filter-city">
                <option value="">Città immobili: tutte</option>
                {options.cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filters.minProps} onChange={e => setFilters(f => ({ ...f, minProps: e.target.value }))} className={selClass} data-testid="bulk-filter-minprops">
                <option value="">N. immobili: tutti</option>
                <option value="0">Senza immobili</option>
                <option value="1">Almeno 1</option>
                <option value="3">Almeno 3</option>
                <option value="5">Almeno 5</option>
                <option value="10">Almeno 10</option>
              </select>
              <select value={filters.activeOnly} onChange={e => setFilters(f => ({ ...f, activeOnly: e.target.value }))} className={selClass} data-testid="bulk-filter-active">
                <option value="">Immobili attivi: tutti</option>
                <option value="si">Con immobili attivi</option>
                <option value="no">Senza immobili attivi</option>
              </select>
              <select value={filters.partner} onChange={e => setFilters(f => ({ ...f, partner: e.target.value }))} className={selClass} data-testid="bulk-filter-partner">
                <option value="">Anche partner: tutti</option>
                <option value="si">Solo partner</option>
                <option value="no">Solo non partner</option>
              </select>
              <button onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-sm text-purple-600 hover:text-purple-800 border border-purple-200 rounded-lg bg-white" data-testid="bulk-filter-reset">
                Azzera filtri
              </button>
            </div>
          </div>

          {/* Selezione */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                Destinatari — <span className="text-purple-700 font-semibold" data-testid="bulk-selected-count">{selected.size} selezionati</span>
                <span className="text-slate-400"> · {filtered.length} filtrati su {managers.length}</span>
              </label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={allFilteredSelected ? clearAll : selectAllFiltered} data-testid="bulk-select-all">
                  {allFilteredSelected ? <><Square className="h-4 w-4 mr-1" /> Deseleziona tutti</> : <><CheckSquare className="h-4 w-4 mr-1" /> Seleziona filtrati</>}
                </Button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="bulk-managers-list">
              {loadingManagers ? (
                <div className="p-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento responsabili...</div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-sm text-slate-400">Nessun responsabile corrisponde ai filtri</div>
              ) : filtered.map(m => (
                <label key={m.id} className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-purple-50 ${selected.has(m.id) ? 'bg-purple-50/70' : ''}`} data-testid={`bulk-manager-row-${m.id}`}>
                  <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)}
                    className="h-4 w-4 rounded border-slate-300 text-purple-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{m.name}{m.is_also_partner && <span className="ml-2 text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">partner</span>}</div>
                    <div className="text-xs text-slate-400 truncate">{m.email}{m.phone ? ` · ${m.phone}` : ''}</div>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 shrink-0">
                    <Building2 className="h-3.5 w-3.5 text-slate-400" />
                    {m.active_property_count}/{m.property_count}
                    {m.property_cities?.length > 0 && <span className="text-slate-400 hidden md:inline"> · {m.property_cities.slice(0, 2).join(', ')}{m.property_cities.length > 2 ? '…' : ''}</span>}
                  </div>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Ogni responsabile riceverà il proprio ticket individuale con notifica email. Immobili: attivi/totali.</p>
          </div>

          {/* Form ticket */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Oggetto *</label>
            <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
              placeholder="Es: Comunicazione importante dall'ufficio" data-testid="bulk-subject-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Priorità</label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className={selClass} data-testid="bulk-priority-select">
                <option value="normale">Normale</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Categoria</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className={selClass} data-testid="bulk-category-select">
                <option value="">Seleziona categoria</option>
                {Object.entries(categories).map(([key, val]) => <option key={key} value={key}>{val.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Messaggio *</label>
            <Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder="Scrivi il messaggio per i responsabili..." rows={5} data-testid="bulk-message-textarea" />
          </div>
          <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${closeAfterSend ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`} data-testid="bulk-close-after-send-label">
            <input type="checkbox" checked={closeAfterSend} onChange={e => setCloseAfterSend(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-amber-600" data-testid="bulk-close-after-send-checkbox" />
            <span className="text-sm">
              <span className="font-medium text-slate-800">Comunicazione senza seguito (invia già chiuso)</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                I ticket vengono creati già chiusi (non restano tra gli aperti dell'ufficio), ma per i responsabili compaiono nella sezione <strong>Comunicazioni da Ufficio</strong> come nuovi da leggere, con email "Hai ricevuto una comunicazione dall'ufficio". Ideale per comunicazioni aziendali che non richiedono risposta.
              </span>
            </span>
          </label>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Allegati</label>
            <input type="file" ref={fileRef} onChange={handleImageUpload} accept="image/*" multiple className="hidden" />
            {attachments.length > 0 && (
              <div className="flex gap-2 mb-2 flex-wrap">
                {attachments.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={img} alt="" className="h-14 w-14 object-cover rounded border" />
                    <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Image className="h-4 w-4 mr-1" /> Aggiungi foto
            </Button>
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 flex items-center justify-between sticky bottom-0 bg-white">
          <span className="text-xs text-slate-500">
            Verranno creati <strong>{selected.size}</strong> ticket individuali
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={handleSend} disabled={sending || selected.size === 0}
              className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bulk-send-btn">
              {sending ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Invio...</> : <><Send className="h-4 w-4 mr-1" /> Invia a {selected.size} responsabili</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
